/*
 * Lemonade Training Oy copy rights all right reserved.
 * RPG / XP / Badge Type Definitions
 */

import { Timestamp } from "firebase/firestore";
import { RadarAxis } from "./movementRegistry";

/**
 * Types of XP-earning events
 */
export const XP_EVENT_TYPE = {
  PR: "pr",
  WORKOUT: "workout",
  BADGE: "badge",
  CASCADE: "cascade",
} as const;

export type XPEventType = (typeof XP_EVENT_TYPE)[keyof typeof XP_EVENT_TYPE];

/**
 * XP event audit trail entry
 * Stored in: Users/{uid}/XPEvents/{eventID}
 *
 * Append-only collection recording every XP-earning event.
 * The AthleteRadarProfile XP total is a materialized view of these events.
 *
 * @example
 * ```typescript
 * const event: XPEvent = {
 *   id: "xp_abc123",
 *   type: "pr",
 *   xpAwarded: 340,
 *   axis: "STR",
 *   movementID: "back-squat",
 *   description: "Back Squat PR: 120 kg",
 *   timestamp: Timestamp.now(),
 * };
 * ```
 */
export type XPEvent = {
  /** Unique event identifier */
  id: string;
  /** What triggered this XP award */
  type: XPEventType;
  /** Amount of XP awarded */
  xpAwarded: number;
  /** Which radar axis this XP applies to */
  axis: RadarAxis;
  /** Related movement ID, if applicable */
  movementID: string | null;
  /** Human-readable event description */
  description: string;
  /** When this event occurred */
  timestamp: Timestamp | Date;
};

/**
 * Badge category for grouping in the badge grid
 */
export const BADGE_CATEGORY = {
  MOVEMENT: "movement",
  LEVEL: "level",
  AXIS: "axis",
} as const;

export type BadgeCategory =
  (typeof BADGE_CATEGORY)[keyof typeof BADGE_CATEGORY];

/**
 * Badge criteria type determining how the badge is earned
 */
export const BADGE_CRITERIA_TYPE = {
  THRESHOLD: "threshold",
  LEVEL: "level",
} as const;

export type BadgeCriteriaType =
  (typeof BADGE_CRITERIA_TYPE)[keyof typeof BADGE_CRITERIA_TYPE];

/**
 * Criteria defining how a badge is earned
 */
export type BadgeCriteria = {
  /** Type of check to perform */
  type: BadgeCriteriaType;
  /** Specific movement ID (for movement-based badges) */
  movementID: string | null;
  /** Specific radar axis (for axis-based badges) */
  axis: RadarAxis | null;
  /** Threshold value to meet (weight, reps, or level) */
  value: number;
};

/**
 * Global badge template defining unlock criteria
 * Stored in: BadgeDefinitions/{badgeID}
 *
 * These are system-defined and shared across all boxes.
 *
 * @example
 * ```typescript
 * const badge: BadgeDefinition = {
 *   id: "100kg-club-back-squat",
 *   name: "100 kg Club",
 *   description: "Back Squat 100 kg or more",
 *   hint: "Squat your way to the century mark",
 *   category: "movement",
 *   criteria: {
 *     type: "threshold",
 *     movementID: "back-squat",
 *     axis: "STR",
 *     value: 100,
 *   },
 *   iconURL: null,
 *   createdAt: Timestamp.now(),
 * };
 * ```
 */
export type BadgeDefinition = {
  /** Unique badge definition ID */
  id: string;
  /** Display name of the badge */
  name: string;
  /** Description of what the badge represents */
  description: string;
  /** Hint text shown for unearned badges */
  hint: string;
  /** Category for grouping in the badge grid */
  category: BadgeCategory;
  /** Criteria for earning this badge */
  criteria: BadgeCriteria;
  /** Optional badge icon URL in Firebase Storage */
  iconURL: string | null;
  /** When this badge definition was created */
  createdAt: Timestamp | Date;
};

/**
 * Earned badge for an athlete
 * Stored in: Users/{uid}/Badges/{badgeID}
 *
 * @example
 * ```typescript
 * const earned: EarnedBadge = {
 *   id: "100kg-club-back-squat",
 *   badgeDefinitionID: "100kg-club-back-squat",
 *   name: "100 kg Club",
 *   description: "Back Squat 100 kg or more",
 *   earnedAt: Timestamp.now(),
 *   movementID: "back-squat",
 *   axis: "STR",
 * };
 * ```
 */
export type EarnedBadge = {
  /** Document ID (matches badgeDefinitionID for simplicity) */
  id: string;
  /** Reference to the badge template */
  badgeDefinitionID: string;
  /** Denormalized badge name for display */
  name: string;
  /** Denormalized badge description for display */
  description: string;
  /** When the badge was earned */
  earnedAt: Timestamp | Date;
  /** Related movement ID, if applicable */
  movementID: string | null;
  /** Related radar axis, if applicable */
  axis: RadarAxis | null;
};

/**
 * Level title thresholds
 * Maps level ranges to display titles
 */
export const LEVEL_TITLES = {
  ROOKIE: "Rookie",
  REGULAR: "Regular",
  COMPETITOR: "Competitor",
  ADVANCED: "Advanced",
  ELITE: "Elite",
  LEGEND: "Legend",
} as const;

export type LevelTitle = (typeof LEVEL_TITLES)[keyof typeof LEVEL_TITLES];

/**
 * Level range definition for title mapping
 */
export type LevelRange = {
  /** Minimum level (inclusive) */
  min: number;
  /** Maximum level (inclusive) */
  max: number;
  /** Display title for this range */
  title: LevelTitle;
};

/**
 * Default level ranges (can be overridden via server config)
 */
export const DEFAULT_LEVEL_RANGES: readonly LevelRange[] = [
  { min: 1, max: 5, title: "Rookie" },
  { min: 6, max: 10, title: "Regular" },
  { min: 11, max: 15, title: "Competitor" },
  { min: 16, max: 20, title: "Advanced" },
  { min: 21, max: 25, title: "Elite" },
  { min: 26, max: 30, title: "Legend" },
] as const;

/**
 * Type guard for XPEvent
 *
 * @example
 * ```typescript
 * if (isXPEvent(data)) {
 *   console.log(data.xpAwarded); // TypeScript knows data is XPEvent
 * }
 * ```
 */
export function isXPEvent(obj: unknown): obj is XPEvent {
  if (typeof obj !== "object" || obj === null) return false;
  const event = obj as XPEvent;
  return (
    typeof event.id === "string" &&
    typeof event.type === "string" &&
    typeof event.xpAwarded === "number" &&
    typeof event.axis === "string"
  );
}

/**
 * Type guard for EarnedBadge
 *
 * @example
 * ```typescript
 * if (isEarnedBadge(data)) {
 *   console.log(data.name); // TypeScript knows data is EarnedBadge
 * }
 * ```
 */
export function isEarnedBadge(obj: unknown): obj is EarnedBadge {
  if (typeof obj !== "object" || obj === null) return false;
  const badge = obj as EarnedBadge;
  return (
    typeof badge.id === "string" &&
    typeof badge.badgeDefinitionID === "string" &&
    typeof badge.name === "string"
  );
}

/**
 * Get the level title for a given level number
 *
 * @example
 * ```typescript
 * getLevelTitle(3);  // "Rookie"
 * getLevelTitle(14); // "Competitor"
 * getLevelTitle(99); // "Legend"
 * ```
 */
export function getLevelTitle(level: number): LevelTitle {
  const range = DEFAULT_LEVEL_RANGES.find(
    (r) => level >= r.min && level <= r.max,
  );
  return range?.title ?? "Legend";
}

/**
 * Maximum level cap. Levels above this are clamped to MAX_LEVEL.
 *
 * Beyond the LEGEND > Lv 30 design intent, headroom is reserved for future
 * expansion (e.g., Mythic / Champion tiers) without a schema change.
 */
export const MAX_LEVEL = 50;

/**
 * XP-per-level curve configuration.
 *
 * Curve: `xpForLevel(L) = base + linear*(L-1) + quadratic*(L-1)^2`
 *
 * This is the XP required to advance FROM level L to level L+1. The curve is
 * monotonically increasing — each level requires more XP than the last —
 * which keeps progression fair without degenerate plateaus.
 *
 * Treated as configuration (not magic numbers) so both Cloud Functions and
 * client UIs derive level/progress from the same single source of truth.
 *
 * Note on tuning: changing these values invalidates historical level
 * snapshots in `AthleteRadarProfile` and per-axis XP totals. Re-derive on
 * deploy if changing.
 */
export const XP_PER_LEVEL = {
  /** Constant term: XP to advance from L=1 to L=2 */
  base: 500,
  /** Linear coefficient on (L-1) */
  linear: 200,
  /** Quadratic coefficient on (L-1)^2 */
  quadratic: 10,
} as const;

/**
 * Materialized level snapshot derived from a cumulative XP total.
 *
 * Used by the Axis Detail drill-down on mobile (LT-311) and web (LT-312) to
 * render the current title, the within-level progress bar, and the XP
 * remaining to the next level.
 *
 * @example
 * ```typescript
 * const info = getLevelInfo(1200);
 * // info.level === 2
 * // info.title === "Rookie"
 * // info.xpInLevel + info.xpToNextLevel === info.xpForNextLevel
 * ```
 */
export type LevelInfo = {
  /** Current level (1..MAX_LEVEL) */
  level: number;
  /** Total XP accumulated */
  totalXp: number;
  /** XP earned within the current level */
  xpInLevel: number;
  /** XP required to advance from the current level to the next */
  xpForNextLevel: number;
  /** XP remaining until the next level-up (0 when at MAX_LEVEL) */
  xpToNextLevel: number;
  /** Progress within current level, 0..1 (1 when at MAX_LEVEL) */
  progressPct: number;
  /** Level title for display (e.g., "Rookie", "Legend") */
  title: LevelTitle;
  /** True when level === MAX_LEVEL */
  isMaxLevel: boolean;
};

/**
 * XP required to advance FROM level L to level L+1.
 *
 * Falls back to `XP_PER_LEVEL.base` for invalid input to keep client code
 * defensive (e.g., a transiently undefined level should still render a
 * non-zero progress bar denominator).
 *
 * @example
 * ```typescript
 * xpForLevel(1); // 500
 * xpForLevel(2); // 710
 * xpForLevel(10); // 3110
 * ```
 */
export function xpForLevel(level: number): number {
  if (!Number.isFinite(level) || level < 1) return XP_PER_LEVEL.base;
  const k = level - 1;
  return (
    XP_PER_LEVEL.base + XP_PER_LEVEL.linear * k + XP_PER_LEVEL.quadratic * k * k
  );
}

/**
 * Cumulative XP needed to reach level N from zero XP.
 *
 * Level 1 requires 0 XP. Level 2 requires `xpForLevel(1)`. Level 3 requires
 * `xpForLevel(1) + xpForLevel(2)`, and so on.
 *
 * @example
 * ```typescript
 * levelToCumulativeXP(1); // 0
 * levelToCumulativeXP(2); // 500
 * levelToCumulativeXP(3); // 1210
 * ```
 */
export function levelToCumulativeXP(level: number): number {
  if (!Number.isFinite(level) || level <= 1) return 0;
  let total = 0;
  for (let L = 1; L < level; L++) total += xpForLevel(L);
  return total;
}

/**
 * Resolve the current level for a given cumulative XP total.
 *
 * Walks the curve until cumulative > xp, returning the prior level. Capped
 * at MAX_LEVEL so out-of-range XP totals don't blow past the title set.
 *
 * @example
 * ```typescript
 * xpToLevel(0);     // 1
 * xpToLevel(499);   // 1
 * xpToLevel(500);   // 2
 * xpToLevel(1210);  // 3
 * ```
 */
export function xpToLevel(xp: number): number {
  if (!Number.isFinite(xp) || xp < 0) return 1;
  let level = 1;
  let cumulative = 0;
  while (level < MAX_LEVEL) {
    const next = cumulative + xpForLevel(level);
    if (xp < next) return level;
    cumulative = next;
    level += 1;
  }
  return MAX_LEVEL;
}

/**
 * Derive per-axis levels from per-axis XP totals using the same curve.
 *
 * Pure function — does no I/O. Safe to call on the client.
 *
 * @example
 * ```typescript
 * computeAxisLevels({ STR: 1500, END: 0, GYM: 750 });
 * // { STR: 3, END: 1, GYM: 2 }
 * ```
 */
export function computeAxisLevels(
  axisXP: Readonly<Record<string, number>> | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!axisXP || typeof axisXP !== "object") return out;
  for (const axis of Object.keys(axisXP)) {
    out[axis] = xpToLevel(axisXP[axis] || 0);
  }
  return out;
}

/**
 * Compute a complete LevelInfo snapshot for a given cumulative XP total.
 *
 * Single derivation path used by both Cloud Functions and client UIs.
 *
 * @example
 * ```typescript
 * const info = getLevelInfo(1500);
 * // { level: 3, totalXp: 1500, xpInLevel: 290, xpForNextLevel: 920, ... }
 * ```
 */
export function getLevelInfo(xp: number): LevelInfo {
  const totalXp = Number.isFinite(xp) && xp >= 0 ? xp : 0;
  const level = xpToLevel(totalXp);
  const isMaxLevel = level >= MAX_LEVEL;
  const xpForNextLevel = xpForLevel(level);
  const cumulativeAtLevel = levelToCumulativeXP(level);
  const xpInLevel = Math.max(0, totalXp - cumulativeAtLevel);
  const xpToNextLevel = isMaxLevel
    ? 0
    : Math.max(0, xpForNextLevel - xpInLevel);
  const progressPct = isMaxLevel
    ? 1
    : xpForNextLevel > 0
      ? Math.min(1, xpInLevel / xpForNextLevel)
      : 0;
  return {
    level,
    totalXp,
    xpInLevel,
    xpForNextLevel,
    xpToNextLevel,
    progressPct,
    title: getLevelTitle(level),
    isMaxLevel,
  };
}
